/**
 * AgentIndicator component - displays agent name badges with color coding
 * 
 * Agent badge colors:
 * - Planner → purple
 * - Coder → blue
 * - Reviewer → green
 * - System → gray
 * - Tool → orange
 * - Orchestrator → indigo
 */
import { cn } from '@/lib/utils'

/**
 * Agent type with corresponding badge colors
 */
type AgentType = 'Planner' | 'Coder' | 'Reviewer' | 'System' | 'Tool' | 'Orchestrator'

/**
 * Badge color classes for each agent type
 */
const agentColors: Record<AgentType, string> = {
  Planner: 'bg-purple-100 text-purple-800',
  Coder: 'bg-blue-100 text-blue-800',
  Reviewer: 'bg-green-100 text-green-800',
  System: 'bg-gray-100 text-gray-600',
  Tool: 'bg-orange-100 text-orange-800',
  Orchestrator: 'bg-indigo-100 text-indigo-800'
}

/**
 * Default color for unknown agent types
 */
const defaultColor = 'bg-gray-100 text-gray-600'

export interface AgentIndicatorProps {
  /** The agent name to display (null/undefined/empty renders nothing) */
  agentName?: string | null
  /** Additional CSS classes */
  className?: string
}

/**
 * Displays a colored badge indicating which agent sent a message.
 * Returns null if agentName is not provided.
 */
export function AgentIndicator({ agentName, className }: AgentIndicatorProps) {
  // Don't render if no agent name
  if (!agentName) {
    return null
  }

  // Get color class for agent type, default to gray for unknown agents
  const colorClass = agentColors[agentName as AgentType] ?? defaultColor

  return (
    <span
      data-agent={agentName}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        colorClass,
        className
      )}
    >
      {agentName}
    </span>
  )
}
